// Sprint 29.8 — first-time onboarding tour for the Enterprise
// Databus surface.
//
// Shows once per principal who has the databus:read permission. Two
// steps: highlight the sidebar section, highlight the "Onboard tenant"
// entry. Persisted via a localStorage flag so dismissals stick across
// reloads but are reset by clearing site data.
//
// Anchors are looked up by `data-tour-id` attributes on the Sidebar.
// Missing anchors short-circuit the tour gracefully — the lookup
// retries on resize / nav so a hydration race doesn't lock anyone out
// of the dismiss path.
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ArrowRight, Sparkles, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useCan } from '@/lib/permissions'

const STORAGE_KEY = 'databus.onboarding-tour.seen'

// Steps are ordered; each names the data-tour-id anchor it attaches
// to. Anchor offsets are computed at render time via getBoundingClientRect.
type TourStep = {
  anchorId: string
  title: string
  body: string
  // Position the popover relative to the anchor. The sidebar lives
  // on the left, so all popovers sit to the right.
  side: 'right'
}

// Fix W1 (nav consolidation): the two-level nav removed the dedicated
// "databus-section-header" sidebar header and the "databus-onboard-link"
// flat sidebar link. The Ingestion module is now a single consolidated
// sidebar entry (data-tour-id="ingestion-consolidated-link") that navigates
// to the Ingestion section; the "Add Connector" wizard lives in the
// horizontal top submenu, which only appears while on an Ingestion route.
//
// We collapsed to one anchored step on the Ingestion sidebar link with copy
// that guides the operator to the top submenu. A second hard-anchored step
// on the top-submenu item was dropped because that element may not be present
// in the DOM when the tour opens (user could be on a non-Ingestion route).
// The graceful fallback (centred popover when anchor is absent) still applies,
// so the tour degrades safely even if the sidebar isn't yet rendered.
const STEPS: TourStep[] = [
  {
    anchorId: 'ingestion-consolidated-link',
    title: 'Enterprise Databus — Ingestion',
    body: 'All tenant adapters, mappings, schemas, recon runs, and DLQ entries are in the Ingestion module. Click "Ingestion" in the left menu, then choose "Add Connector" from the top submenu to onboard a new tenant.',
    side: 'right',
  },
]

export function OnboardingTour() {
  // Gate strictly on databus:read so we don't pop the tour for
  // analysts who have no databus surface to navigate to.
  const canSee = useCan('databus:read')
  const [active, setActive] = useState(false)
  const [step, setStep] = useState(0)

  useEffect(() => {
    if (!canSee) return
    if (typeof window === 'undefined') return
    if (window.localStorage.getItem(STORAGE_KEY)) return
    // Defer mount so the sidebar has time to render its anchors.
    const t = window.setTimeout(() => setActive(true), 600)
    return () => window.clearTimeout(t)
  }, [canSee])

  if (!active) return null

  const current = STEPS[step]
  if (!current) return null

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1')
    } catch {
      /* private mode / quota — best-effort */
    }
    setActive(false)
  }

  const advance = () => {
    if (step + 1 >= STEPS.length) {
      dismiss()
      return
    }
    setStep(step + 1)
  }

  return (
    <TourStepOverlay step={current} index={step} total={STEPS.length} onSkip={dismiss} onNext={advance} />
  )
}

// TourStepOverlay paints a dim backdrop, an outline ring around the
// anchored element, and a popover next to it. Anchor lookup retries
// once per resize so the tour adapts to layout changes (sidebar
// drawer toggle on mobile, etc).
function TourStepOverlay({
  step,
  index,
  total,
  onSkip,
  onNext,
}: {
  step: TourStep
  index: number
  total: number
  onSkip: () => void
  onNext: () => void
}) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  const tickRef = useRef(0)
  const popoverRef = useRef<HTMLDivElement>(null)
  const skipBtnRef = useRef<HTMLButtonElement>(null)

  // Focus the Skip button on mount. The tour auto-opens (no
  // user-controlled trigger) so we don't need to restore focus on
  // close — the page that was under the operator's cursor still has
  // it after dismiss.
  useEffect(() => {
    skipBtnRef.current?.focus()
  }, [])

  // Escape dismisses the tour. Document-level listener so the
  // operator can hit Esc even if focus drifts onto the backdrop.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onSkip()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onSkip])

  // Trap Tab / Shift-Tab inside the popover so keyboard users can't
  // wander into the page behind the backdrop while the tour is open.
  function onKeyDownTrap(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab') return
    const root = popoverRef.current
    if (!root) return
    const focusables = root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (!first || !last) return
    const activeEl = document.activeElement as HTMLElement | null
    if (e.shiftKey && activeEl === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && activeEl === last) {
      e.preventDefault()
      first.focus()
    }
  }

  useLayoutEffect(() => {
    function probe() {
      const el = document.querySelector(`[data-tour-id="${step.anchorId}"]`)
      if (el instanceof HTMLElement) {
        setRect(el.getBoundingClientRect())
      } else {
        setRect(null)
      }
    }
    probe()
    // Cheap retry loop: re-probe a handful of times in case the
    // sidebar is in mid-animation. After 10 ticks (~1s) we give up
    // and let the resize listener cover the rest.
    tickRef.current = 0
    const id = window.setInterval(() => {
      tickRef.current += 1
      probe()
      if (tickRef.current > 10) window.clearInterval(id)
    }, 100)
    const onResize = () => probe()
    window.addEventListener('resize', onResize)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('resize', onResize)
    }
  }, [step.anchorId])

  // Anchor not (yet) visible — render the popover centred so the
  // operator can still dismiss the tour.
  const popoverPos = rect
    ? {
        top: Math.max(16, rect.top + rect.height / 2 - 80),
        left: rect.right + 16,
      }
    : { top: 80, left: 80 }

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="tour-title">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onSkip} aria-hidden />

      {/* Anchor outline */}
      {rect ? (
        <div
          className="pointer-events-none absolute rounded-md ring-2 ring-primary ring-offset-2 ring-offset-background"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
          }}
          aria-hidden
        />
      ) : null}

      {/* Popover — the keydown handler traps Tab focus inside the
          popover; activation is via the buttons within. The wrapper
          itself stays non-interactive (no onClick), so the
          static-element-interactions disable is the right escape
          hatch. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- focus-trap container; activation lives on the buttons inside */}
      <div
        ref={popoverRef}
        onKeyDown={onKeyDownTrap}
        className="absolute w-80 rounded-lg border border-border bg-card p-4 shadow-2xl"
        style={popoverPos}
      >
        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="h-3 w-3" aria-hidden />
          <span>
            Tour · step {index + 1} of {total}
          </span>
          <button
            type="button"
            onClick={onSkip}
            aria-label="Close tour"
            className="ml-auto rounded p-0.5 hover:bg-muted"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </div>
        <h3 id="tour-title" className="mb-1 text-sm font-semibold">
          {step.title}
        </h3>
        <p className="mb-3 text-sm text-muted-foreground">{step.body}</p>
        <div className="flex items-center justify-between">
          <Button ref={skipBtnRef} variant="ghost" size="sm" onClick={onSkip}>
            Skip
          </Button>
          <Button size="sm" onClick={onNext}>
            {index + 1 === total ? 'Got it' : 'Next'}
            <ArrowRight className="ml-1 h-3 w-3" aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  )
}
